import { UserPlus, MessageSquare, BarChart3, Trophy, ArrowRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { SectionHeading } from "@/components/marketing/SectionHeading";

const steps = [
  {
    number: "01",
    icon: UserPlus,
    title: "Create Your Account",
    description: "Sign up in seconds and access your personalized dashboard.",
    color: "primary",
  },
  {
    number: "02",
    icon: MessageSquare,
    title: "Start a Role-Play",
    description: "Choose a scenario and engage with our AI buyer through voice or text. Practice role-plays, objection drills, or speed-sells.",
    color: "accent",
  },
  {
    number: "03",
    icon: BarChart3,
    title: "Get Instant Feedback",
    description: "Receive detailed scoring across multiple sales performance categories, voice and visual analytics with actionable coaching tips.",
    color: "purple",
  },
  {
    number: "04",
    icon: Trophy,
    title: "Track & Improve",
    description: "Monitor your progress over time, identify weak areas, and watch your scores climb as you practice.",
    color: "emerald",
  },
];

const StepCard = ({ step, index, totalSteps }: { step: typeof steps[0]; index: number; totalSteps: number }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  
  const colorClasses = {
    neutral: {
      icon: "text-primary",
      iconBg: "bg-muted/40 border border-subtle group-hover:border-primary/30",
      number: "from-foreground/60 to-foreground/20",
      glow: "group-hover:shadow-elevation-2",
      border: "group-hover:border-border",
      arrow: "text-muted-foreground/40",
    },
  };

  const colors = colorClasses.neutral;
  const Icon = step.icon;

  return (
    <div 
      ref={ref}
      className={`relative group transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 0.15}s` }}
    >
      {/* Arrow Connector (desktop only) */}
      {index < totalSteps - 1 && (
        <div className="hidden lg:flex absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
          <ArrowRight className={`w-6 h-6 ${colors.arrow}`} />
        </div>
      )}
      
      <div className={`rounded-2xl bg-card/70 backdrop-blur-sm p-6 sm:p-7 h-full border border-subtle shadow-elevation-1 transition-all duration-300 hover:shadow-elevation-2 ${colors.border}`}>
        {/* Top Row: Number + Icon */}
        <div className="flex items-center justify-between mb-5">
          {/* Gradient Number */}
          <span className="font-sans tabular-nums text-2xl font-medium tracking-tight text-muted-foreground/60">
            {step.number}
          </span>
          
          {/* Icon with glow effect */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-200 ${colors.iconBg} ${colors.icon}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        
        {/* Divider */}
        <div className="h-px w-full bg-[hsl(var(--border-subtle))] mb-5" />
        
        {/* Content */}
        <h3 className="font-display text-lg mb-2 text-foreground">
          {step.title}
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {step.description}
        </p>
      </div>
    </div>
  );
};

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-20 sm:py-28 relative">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <SectionHeading
          eyebrow="Simple process"
          title={<>How it <span className="text-primary">works</span></>}
          description="From signup to sales mastery in four steps."
        />

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {steps.map((step, index) => (
            <StepCard key={step.number} step={step} index={index} totalSteps={steps.length} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
